<template>
  <div class="wrapper">
    <div class="header">
      <TabButton :active="true" :on_click="() => {}" text="CROSS TAB" />

      <div class="download-wrapper" v-if="canDownload">
        <Loading v-if="loadingDownload" :size="24" />

        <button :disabled="loadingDownload" type="button" @click="download">
          <DownloadIcon />
          Download
        </button>
      </div>
    </div>

    <div class="content">
      <div class="with-load" v-if="!loadingOptions">
        <div class="no-option" v-if="options.length < 2">
          {{ parseTranslation(NO_OPTIONS) }}
        </div>
        <div v-else class="options">
          <div class="data-row">
            <div class="label"></div>
            <div class="question">{{ parseTranslation(QUESTION) }}</div>
            <div class="banner">{{ parseTranslation(BANNER) }}</div>
          </div>

          <div v-for="option in options" :key="option.code" class="data-row">
            <div class="label">{{ `${option.code} - ${option.question}` }}</div>

            <div class="question">
              <input
                type="radio"
                :checked="selected[option.code] === 'questions'"
                :name="option.code"
                :id="`${option.code}#questions`"
                @click="handleRadioClick"
              />
            </div>

            <div class="banner">
              <input
                type="radio"
                :checked="selected[option.code] === 'banners'"
                :name="option.code"
                :id="`${option.code}#banners`"
                :disabled="questionsConfig[option.type].hasSubQuestion"
                @click="handleRadioClick"
              />
            </div>
          </div>
        </div>
      </div>
      <div v-else class="loading-option">
        <div
          v-for="(count, index) in Array.from({ length: 10 })"
          :key="`shimmer-${index}`"
          class="shimmer"
        ></div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapActions } from 'vuex'

import TabButton from '../TabButton.vue'
import DownloadIcon from '@/components/imgs/Download2'
import Loading from '@/components/loading/Loader-4'

import { downloadBuffer } from '@/utils/download'
import { questionsConfig } from '@/utils/questions'

import * as translationIndex from './index.translations'

const { default: translations, ...translationKeys } = translationIndex

export default {
  name: 'SurveyExports',

  components: {
    TabButton,
    DownloadIcon,
    Loading,
  },

  props: {
    survey_status: {
      required: true,
      type: Object,
    },
  },

  data() {
    return {
      translations,
      ...translationKeys,
      loadingOptions: true,
      loadingDownload: false,
      questionsConfig,
      options: [],
      selected: {},
    }
  },

  computed: {
    canDownload() {
      const atLeastOneOfEach = ['banners', 'questions'].every(type =>
        Object.values(this.selected).find(chosenType => chosenType === type),
      )

      return atLeastOneOfEach
    },
  },

  methods: {
    ...mapActions(['executeSurveyAPICall', 'sendMessage']),

    async getOptions() {
      this.loadingOptions = true

      const { survey } = this.$route.query || {}

      const options = await this.executeSurveyAPICall({
        path: `report/cross-tab/options`,

        queryParams: {
          survey: survey,
        },
      })

      this.loadingOptions = false

      this.options = options || []
    },

    async download() {
      this.loadingDownload = true

      const { survey } = this.$route.query || {}

      const [banners, questions] = ['banners', 'questions'].map(type =>
        Object.entries(this.selected)
          .filter(([, selectedType]) => type === selectedType)
          .map(([code]) => code),
      )

      const buffer = await this.executeSurveyAPICall({
        path: `report/cross-tab`,

        responseType: 'blob',

        method: 'POST',

        payload: {
          id: survey,
          banners,
          questions,
          significance: true,
        },
      })

      this.loadingDownload = false

      downloadBuffer(`cross-tab-${new Date().toISOString()}.xlsx`, buffer)
    },

    selectType(code, type) {
      this.selected = {
        ...this.selected,
        [code]: type,
      }
    },

    removeSelection(code) {
      const updated = {
        ...this.selected,
      }

      delete updated[code]

      this.selected = updated
    },

    handleRadioClick({ target }) {
      const { id } = target

      const [code, type] = id.split('#')

      const currentType = this.selected[code]

      if (!currentType) return this.selectType(code, type)

      const isRemoval = currentType === type

      if (!isRemoval)
        return this.selectType(code, currentType === 'banners' ? 'questions' : 'banners')

      this.removeSelection(code)
    },
  },

  created() {
    this.getOptions()
  },

  updated() {
    console.log(this.selected)
  },
}
</script>

<style lang="scss" scoped>
.no {
  color: red;
}

.content {
  margin: 24px auto 0;
  background: hsl(0deg, 0%, 95%);
  border-radius: 6px;
  padding: 16px;
}

.no-option {
  text-align: center;
}

.shimmer {
  animation: shimmer-cross 1.65s infinite;
  background: linear-gradient(to right, #ddd 4%, #e2e2e2 25%, #ddd 36%);
  background-size: 1000px 100%;
  height: 45px;
  width: 100%;
  border-radius: 8px;

  & + & {
    margin-top: 16px;
  }
}

@keyframes shimmer-cross {
  0% {
    background-position: -1000px 0;
  }
  100% {
    background-position: 1000px 0;
  }
}

.header,
.data-row {
  display: flex;
  align-items: center;
}

.download-wrapper {
  margin-left: auto;

  display: flex;
  align-items: center;
  gap: 12px;

  button {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    color: white;
    background: $blue;
    padding: 4px 12px;
    border-radius: 999px;
    border: none;
    transition: background-color 0.35s ease;

    svg {
      width: 16px;
      height: 16px;
    }

    &:hover {
      background: $secondary-two;
    }
  }
}

.data-row {
  & + & {
    margin-top: 24px;
  }

  .label {
    width: 70%;
  }

  .question,
  .banner {
    width: 15%;
    justify-content: center;
    display: flex;
    align-items: center;

    input {
      cursor: pointer;
    }
  }
}
</style>
